import type { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Scene } from "@babylonjs/core/scene";
import type { Aabb, ArenaKind, ArenaTeam } from "@townsy/contracts";
import { arenaLayoutOf, arenaStandSpots, baseballWallDistance, type ArenaLayout, type ArenaStand } from "@townsy/world";
import { PartBatch, cylinder, torus } from "../../../scene/core/meshKit";
import { makeDynamicTexture } from "../../../scene/core/materials";
import { CROWD_RIPPLE, crowdRippleLift } from "./cheers";
import { paintField, surfaceMap, type Ctx } from "./paint";

/**
 * A stadium's static 3D (ADR-0044) on top of the generic room: the painted playing surface, the boards or walls around
 * it, the goals / posts / hoops / nets, the stands with their seat rows (home seats in the home team's colour), the
 * light towers or the indoor lighting ring, and the big scoreboard frame. The `venue_arena_*` colliders (the field and
 * the stands) are drawn here; the generic builder skips them. Static parts are merged into one vertex-coloured mesh.
 */
const C = {
  concrete: "#8E949E",
  concreteDark: "#6E747E",
  riser: "#5A6070",
  seatNeutral: "#4E5C78",
  seatCrowd: "#56627A",
  steel: "#3A3F4A",
  board: "#1B2233",
  white: "#F4F4F0",
  yellow: "#F2C230",
  padding: "#2E6B3A",
  red: "#C8302B",
  glassFrame: "#C9D1DC",
  lamp: "#FFF6D8",
} as const;

export interface StadiumHandle {
  meshes: Mesh[];
  /** Shadow casters among them. */
  casters: Mesh[];
  fieldTexture: DynamicTexture;
  repaintField(home: ArenaTeam, awayName: string): void;
  screens: { texture: DynamicTexture; width: number; height: number };
  dispose(): void;
}

/** Steps of a stand: one box per row (tread height), a seat strip on each, a back wall. */
function addStand(b: PartBatch, s: ArenaStand, seatColor: string): void {
  const fwd = { x: Math.sin(s.facing), z: Math.cos(s.facing) };
  const alongX = Math.abs(fwd.z) > 0.5;
  const box = s.box;
  const lateral = alongX ? box.maxX - box.minX : box.maxZ - box.minZ;
  const lateralMid = alongX ? (box.minX + box.maxX) / 2 : (box.minZ + box.maxZ) / 2;
  // The front edge along the facing axis, rows going back (away from the field).
  const front = alongX ? (fwd.z > 0 ? box.maxZ : box.minZ) : fwd.x > 0 ? box.maxX : box.minX;
  const back = -1;
  const along = (d: number) => front + back * (alongX ? fwd.z : fwd.x) * d;
  for (let row = 0; row < s.rows; row++) {
    const h = s.firstRowY + row * s.rowRise;
    const mid = along(row * s.rowDepth + s.rowDepth / 2);
    const cx = alongX ? lateralMid : mid;
    const cz = alongX ? mid : lateralMid;
    const w = alongX ? lateral : s.rowDepth;
    const d = alongX ? s.rowDepth : lateral;
    b.boxBase(w, h, d, row % 2 === 0 ? C.concrete : C.concreteDark, cx, 0, cz);
    // The seat strip on the back half of the tread, facing the field.
    const seatMid = along(row * s.rowDepth + s.rowDepth * 0.72);
    b.boxBase(alongX ? lateral - 0.2 : 0.36, 0.42, alongX ? 0.36 : lateral - 0.2, seatColor, alongX ? lateralMid : seatMid, h, alongX ? seatMid : lateralMid);
  }
  // Back wall behind the top row: low enough that the walk camera behind a seated fan looks over it.
  const top = s.firstRowY + (s.rows - 1) * s.rowRise;
  const wallAt = along(s.rows * s.rowDepth + 0.15);
  b.boxBase(alongX ? lateral : 0.3, top + 1.0, alongX ? 0.3 : lateral, C.riser, alongX ? lateralMid : wallAt, 0, alongX ? wallAt : lateralMid);
}

/** A ring of boxes along a path (boards, walls), each segment `height` tall from `y0`. */
function addPath(b: PartBatch, pts: ReadonlyArray<{ x: number; z: number }>, thickness: number, height: number, y0: number, color: string, closed = false): void {
  const n = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < n; i++) {
    const a = pts[i]!;
    const c = pts[(i + 1) % pts.length]!;
    const len = Math.hypot(c.x - a.x, c.z - a.z);
    if (len < 1e-3) continue;
    const yaw = Math.atan2(c.x - a.x, c.z - a.z);
    b.boxBase(thickness, height, len + thickness * 0.5, color, (a.x + c.x) / 2, y0, (a.z + c.z) / 2, yaw);
  }
}

/** The rink's rounded rectangle as a closed path. */
function rinkPath(cx: number, cz: number, hx: number, hz: number, r: number, steps = 8): Array<{ x: number; z: number }> {
  const pts: Array<{ x: number; z: number }> = [];
  const corners = [
    { x: hx - r, z: hz - r, a0: 0 },
    { x: -(hx - r), z: hz - r, a0: Math.PI / 2 },
    { x: -(hx - r), z: -(hz - r), a0: Math.PI },
    { x: hx - r, z: -(hz - r), a0: Math.PI * 1.5 },
  ];
  for (const c of corners) {
    for (let k = 0; k <= steps; k++) {
      const a = c.a0 + (k / steps) * (Math.PI / 2);
      pts.push({ x: cx + c.x + Math.cos(a) * r, z: cz + c.z + Math.sin(a) * r });
    }
  }
  return pts;
}

/** Goalposts, goals, hoops and the like (static, vertex coloured). Nets go to `nets`. */
function addFixtures(scene: Scene, b: PartBatch, l: ArenaLayout, nets: PartBatch, glass: PartBatch, home: ArenaTeam): void {
  const f = l.field;
  switch (f.sport) {
    case "football": {
      for (const s of [-1, 1]) {
        const x = f.cx + s * f.endLineX;
        const gp = f.goalpost;
        b.boxBase(0.6, 0.9, 0.6, C.padding, x + s * 1.6, 0, f.cz);
        b.add(cylinder(scene, "fbPost", { height: gp.crossbarY, diameter: 0.18 }, C.yellow, { pos: { x: x + s * 1.6, y: gp.crossbarY / 2, z: f.cz } }));
        b.box(1.6, 0.14, 0.14, C.yellow, x + s * 0.8, gp.crossbarY, f.cz);
        b.box(0.14, 0.14, gp.width, C.yellow, x, gp.crossbarY, f.cz);
        for (const t of [-1, 1]) b.box(0.12, gp.uprightTop - gp.crossbarY, 0.12, C.yellow, x, (gp.uprightTop + gp.crossbarY) / 2, f.cz + (t * gp.width) / 2);
      }
      return;
    }
    case "soccer": {
      for (const s of [-1, 1]) {
        const x = f.cx + s * (f.length / 2);
        const w = f.goal.width;
        const h = f.goal.height;
        for (const t of [-1, 1]) b.box(0.12, h, 0.12, C.white, x, h / 2, f.cz + (t * (w + 0.12)) / 2);
        b.box(0.12, 0.12, w + 0.24, C.white, x, h, f.cz);
        // The net: back, roof and sides 2 m deep.
        const nx = x + s * 2;
        nets.box(0.02, h, w, C.white, nx, h / 2, f.cz);
        nets.box(2, 0.02, w, C.white, x + s, h, f.cz);
        for (const t of [-1, 1]) nets.box(2, h, 0.02, C.white, x + s, h / 2, f.cz + (t * w) / 2);
      }
      return;
    }
    case "basketball": {
      for (const s of [-1, 1]) {
        const rimX = f.cx + s * f.hoopX;
        const boardX = f.cx + s * f.backboardX;
        const baseX = f.cx + s * (f.length / 2 + 1.6);
        // Stanchion: padded base, arm, backboard, rim, net.
        b.boxBase(1.2, 1.1, 1.0, home.primary, baseX, 0, f.cz);
        b.box(0.22, 2.4, 0.22, C.steel, baseX, 2.3, f.cz);
        b.box(Math.abs(baseX - boardX), 0.18, 0.18, C.steel, (baseX + boardX) / 2, 3.45, f.cz);
        b.box(0.05, 1.07, 1.83, C.white, boardX, 3.45, f.cz);
        glass.box(0.03, 1.07, 1.83, C.white, boardX - s * 0.01, 3.45, f.cz);
        b.box(0.06, 0.45, 0.59, C.red, boardX - s * 0.03, 3.3, f.cz);
        b.add(torus(scene, "bbRim", f.rimRadius * 2, 0.03, 18, "#E8562A", { pos: { x: rimX, y: f.rimY, z: f.cz } }));
        b.box(Math.abs(rimX - boardX) - f.rimRadius, 0.04, 0.12, "#E8562A", (rimX + boardX) / 2 + s * f.rimRadius * 0.5, f.rimY, f.cz);
        nets.add(cylinder(scene, "bbNet", { height: 0.42, diameterTop: f.rimRadius * 2, diameterBottom: f.rimRadius * 1.3, tessellation: 12 }, C.white, { pos: { x: rimX, y: f.rimY - 0.21, z: f.cz } }));
      }
      return;
    }
    case "hockey": {
      for (const s of [-1, 1]) {
        const x = f.cx + s * f.goalLineX;
        const w = f.goal.width;
        const h = f.goal.height;
        for (const t of [-1, 1]) b.box(0.06, h, 0.06, C.red, x, h / 2, f.cz + (t * w) / 2);
        b.box(0.06, 0.06, w, C.red, x, h, f.cz);
        b.box(1.0, 0.05, 0.05, C.red, x + s * 0.5, 0.03, f.cz - w / 2);
        b.box(1.0, 0.05, 0.05, C.red, x + s * 0.5, 0.03, f.cz + w / 2);
        nets.box(0.02, h, w, C.white, x + s * 1.0, h / 2, f.cz);
        nets.box(1.0, 0.02, w, C.white, x + s * 0.5, h, f.cz);
        for (const t of [-1, 1]) nets.box(1.0, h, 0.02, C.white, x + s * 0.5, h / 2, f.cz + (t * w) / 2);
      }
      return;
    }
    case "baseball": {
      // The mound, the dugouts, the foul poles.
      b.add(cylinder(scene, "bbMound", { height: 0.25, diameterTop: 3.2, diameterBottom: 5.4 }, "#B9824F", { pos: { x: f.mound.x, y: 0.125, z: f.mound.z } }));
      b.box(0.6, 0.06, 0.15, C.white, f.mound.x, 0.27, f.mound.z);
      for (const s of [-1, 1]) {
        const dx = s * 28;
        b.boxBase(2.2, 0.5, 14, C.board, dx, 0, f.home.z - 0.5);
        b.boxBase(0.4, 2.4, 14, C.board, dx + s * 1.2, 0, f.home.z - 0.5);
        b.box(2.8, 0.12, 14.4, home.primary, dx + s * 0.2, 2.4, f.home.z - 0.5);
        const pole = { x: f.home.x + s * f.wall.pole * Math.SQRT1_2, z: f.home.z + f.wall.pole * Math.SQRT1_2 };
        b.add(cylinder(scene, "bbPole", { height: 14, diameter: 0.25 }, C.yellow, { pos: { x: pole.x, y: 7, z: pole.z } }));
      }
      return;
    }
  }
}

export function buildStadium(scene: Scene, kind: ArenaKind, home: ArenaTeam, awayName: string): StadiumHandle {
  const l = arenaLayoutOf(kind);
  const f = l.field;
  const meshes: Mesh[] = [];
  const materials: StandardMaterial[] = [];
  const vc = new StandardMaterial("arenaVc", scene);
  vc.diffuseColor = Color3.White();
  vc.specularColor = new Color3(0.05, 0.05, 0.05);
  vc.ambientColor = Color3.White();
  materials.push(vc);

  // ---- the playing surface (painted), lifted off the room floor ----
  const paintRect: Aabb =
    f.sport === "baseball" ? { minX: l.room.minX + 0.5, minZ: f.backstopZ - 0.2, maxX: l.room.maxX - 0.5, maxZ: f.home.z + f.wall.centre + 2.2 } : { minX: l.fence.minX, minZ: l.fence.minZ, maxX: l.fence.maxX, maxZ: l.fence.maxZ };
  const map = surfaceMap(paintRect, 2048, f.sport === "basketball" ? 56 : f.sport === "hockey" ? 30 : 20);
  const fieldTexture = makeDynamicTexture(scene, "arenaFieldTex", map.width, map.height);
  fieldTexture.wrapU = Texture.CLAMP_ADDRESSMODE;
  fieldTexture.wrapV = Texture.CLAMP_ADDRESSMODE;
  const repaintField = (h: ArenaTeam, a: string) => {
    paintField(fieldTexture.getContext() as unknown as Ctx, map, f, h, a);
    fieldTexture.update();
  };
  repaintField(home, awayName);
  const fieldMat = new StandardMaterial("arenaFieldMat", scene);
  fieldMat.diffuseTexture = fieldTexture;
  fieldMat.specularColor = f.sport === "hockey" || f.sport === "basketball" ? new Color3(0.25, 0.25, 0.25) : new Color3(0.03, 0.03, 0.03);
  fieldMat.zOffset = -2;
  materials.push(fieldMat);
  const ground = CreateGround("arenaField", { width: paintRect.maxX - paintRect.minX, height: paintRect.maxZ - paintRect.minZ }, scene);
  ground.position.set((paintRect.minX + paintRect.maxX) / 2, 0.012, (paintRect.minZ + paintRect.maxZ) / 2);
  ground.material = fieldMat;
  ground.receiveShadows = true;
  ground.isPickable = false;
  ground.freezeWorldMatrix();
  meshes.push(ground);

  const b = new PartBatch();
  const nets = new PartBatch();
  const glass = new PartBatch();

  // ---- boards and walls around the field ----
  if (f.sport === "hockey") {
    const path = rinkPath(f.cx, f.cz, f.length / 2 + 0.1, f.width / 2 + 0.1, f.cornerRadius);
    addPath(b, path, 0.16, 0.9, 0, C.white, true);
    addPath(b, path, 0.17, 0.18, 0, C.yellow, true);
    addPath(b, path, 0.1, 0.08, 0.92, home.primary, true);
    addPath(glass, path, 0.03, 1.8, 0.98, C.glassFrame, true);
  } else if (f.sport === "baseball") {
    // The outfield wall along its arc, padded green, and the low fences along the foul lines to the stands.
    const pts: Array<{ x: number; z: number }> = [];
    for (let i = 0; i <= 40; i++) {
      const yaw = -Math.PI / 4 + (i / 40) * (Math.PI / 2);
      const d = baseballWallDistance(f, yaw);
      pts.push({ x: f.home.x + Math.sin(yaw) * d, z: f.home.z + Math.cos(yaw) * d });
    }
    addPath(b, pts, 0.4, f.wall.height, 0, C.padding);
    addPath(b, pts, 0.42, 0.12, f.wall.height, C.yellow);
    for (const s of [-1, 1]) {
      addPath(b, [{ x: s * 30, z: f.backstopZ }, { x: s * 30, z: -12 }], 0.2, 1.1, 0, C.board);
      addPath(b, [{ x: s * 30, z: -12 }, pts[s < 0 ? 0 : pts.length - 1]!], 0.2, 1.1, 0, C.board);
    }
    addPath(b, [{ x: -30, z: f.backstopZ }, { x: 30, z: f.backstopZ }], 0.25, 1.2, 0, C.board);
    // The backstop net behind home plate.
    nets.boxBase(24, 6, 0.04, C.white, f.home.x, 1.2, f.backstopZ - 0.1);
  } else {
    // Low advertising boards just inside the fence, panels in the home colour and dark blue.
    const fe = l.fence;
    const inset = 0.6;
    const h = f.sport === "basketball" ? 0.75 : 1.0;
    const corners = [
      { x: fe.minX + inset, z: fe.minZ + inset },
      { x: fe.maxX - inset, z: fe.minZ + inset },
      { x: fe.maxX - inset, z: fe.maxZ - inset },
      { x: fe.minX + inset, z: fe.maxZ - inset },
    ];
    addPath(b, corners, 0.18, h, 0, C.board, true);
    // Colour panels on the boards' field side.
    const len = fe.maxX - fe.minX - 2 * inset;
    const panels = Math.floor(len / 6);
    for (let i = 0; i < panels; i++) {
      const x = fe.minX + inset + 3 + i * 6;
      for (const z of [fe.minZ + inset + 0.1, fe.maxZ - inset - 0.1]) b.box(5.6, h * 0.62, 0.03, i % 2 ? home.primary : home.secondary, x, h * 0.5, z);
    }
  }

  addFixtures(scene, b, l, nets, glass, home);

  // ---- the stands ----
  for (const s of l.stands) addStand(b, s, s.section === "home" ? home.primary : s.section === "away" ? C.seatNeutral : C.seatCrowd);

  // ---- lights: towers outdoors, lamps hung over the floor indoors (no truss, so the overview sees the play) ----
  const lamps = new PartBatch();
  if (l.indoor) {
    const ringHx = f.length / 2 + 6;
    const ringHz = f.width / 2 + 6;
    const ring = [
      { x: f.cx - ringHx, z: f.cz - ringHz },
      { x: f.cx + ringHx, z: f.cz - ringHz },
      { x: f.cx + ringHx, z: f.cz + ringHz },
      { x: f.cx - ringHx, z: f.cz + ringHz },
    ];
    for (const p of ring) lamps.box(0.9, 0.3, 0.9, C.lamp, p.x, 14.8, p.z);
    for (let k = -3; k <= 3; k++) {
      lamps.box(0.9, 0.3, 0.9, C.lamp, f.cx + k * (ringHx / 3.5), 14.8, f.cz - ringHz);
      lamps.box(0.9, 0.3, 0.9, C.lamp, f.cx + k * (ringHx / 3.5), 14.8, f.cz + ringHz);
    }
  } else {
    const r = l.room;
    for (const [x, z] of [
      [r.minX + 4, r.minZ + 4],
      [r.maxX - 4, r.minZ + 4],
      [r.minX + 4, r.maxZ - 4],
      [r.maxX - 4, r.maxZ - 4],
    ] as const) {
      b.add(cylinder(scene, "arenaTower", { height: 26, diameterTop: 0.6, diameterBottom: 1.2 }, C.steel, { pos: { x, y: 13, z } }));
      const toward = Math.atan2(f.cx - x, f.cz - z);
      b.box(6, 3.2, 0.6, C.steel, x, 26.5, z, toward);
      for (let i = 0; i < 4; i++) for (let j = 0; j < 2; j++) lamps.box(1.1, 1.1, 0.2, C.lamp, x + Math.cos(toward) * (i - 1.5) * 1.4 + Math.sin(toward) * 0.35, 25.9 + j * 1.3, z - Math.sin(toward) * (i - 1.5) * 1.4 + Math.cos(toward) * 0.35, toward);
    }
  }

  // ---- the scoreboard frame and screen ----
  const sb = l.scoreboard;
  const screenTex = makeDynamicTexture(scene, "arenaScoreTex", sb.style === "center" ? 640 : 1280, sb.style === "center" ? 356 : 542);
  const screenMat = new StandardMaterial("arenaScoreMat", scene);
  screenMat.disableLighting = true;
  screenMat.emissiveTexture = screenTex;
  screenMat.diffuseColor = Color3.Black();
  screenMat.specularColor = Color3.Black();
  materials.push(screenMat);
  const fwd = { x: Math.sin(sb.facing), z: Math.cos(sb.facing) };
  if (sb.style === "center") {
    // A four-sided cube hung over the court.
    const half = sb.width / 2;
    b.boxBase(sb.width + 0.4, sb.height + 0.6, sb.width + 0.4, C.board, sb.x, sb.bottomY - 0.3, sb.z);
    for (const [dx, dz] of [
      [-0.3, 0],
      [0.3, 0],
      [0, -0.3],
      [0, 0.3],
    ] as const) b.box(0.05, 15 - sb.bottomY - sb.height, 0.05, C.steel, sb.x + dx * sb.width, (15 + sb.bottomY + sb.height) / 2, sb.z + dz * sb.width);
    for (let k = 0; k < 4; k++) {
      const yaw = (k * Math.PI) / 2;
      const p = CreatePlane(`arenaScreen${k}`, { width: sb.width, height: sb.height }, scene);
      p.position.set(sb.x + Math.sin(yaw) * (half + 0.21), sb.bottomY + sb.height / 2, sb.z + Math.cos(yaw) * (half + 0.21));
      // A plane's front faces -z: turn it to face outwards.
      p.rotation.y = yaw + Math.PI;
      p.material = screenMat;
      p.isPickable = false;
      meshes.push(p);
    }
  } else {
    const back = { x: sb.x - fwd.x * 0.4, z: sb.z - fwd.z * 0.4 };
    // The screen faces north or south (along z) or east or west (along x); cos(PI/2) is 6e-17, not 0.
    const facesZ = Math.abs(fwd.z) > 0.5;
    b.box(facesZ ? sb.width + 1 : 0.6, sb.height + 1, facesZ ? 0.6 : sb.width + 1, C.board, back.x, sb.bottomY + sb.height / 2, back.z);
    for (const t of [-0.35, 0.35]) {
      const px = back.x + (facesZ ? t * sb.width : 0);
      const pz = back.z + (facesZ ? 0 : t * sb.width);
      b.boxBase(0.7, sb.bottomY, 0.7, C.steel, px, 0, pz);
    }
    const p = CreatePlane("arenaScreen", { width: sb.width, height: sb.height }, scene);
    p.position.set(sb.x - fwd.x * 0.05, sb.bottomY + sb.height / 2, sb.z - fwd.z * 0.05);
    p.rotation.y = sb.facing + Math.PI;
    p.material = screenMat;
    p.isPickable = false;
    meshes.push(p);
  }

  const merged = b.build("arenaStatic", scene);
  const casters: Mesh[] = [];
  if (merged) {
    merged.material = vc;
    merged.receiveShadows = true;
    merged.freezeWorldMatrix();
    meshes.push(merged);
    casters.push(merged);
  }
  const lampMesh = lamps.build("arenaLamps", scene);
  if (lampMesh) {
    const lampMat = new StandardMaterial("arenaLampMat", scene);
    lampMat.emissiveColor = Color3.FromHexString(C.lamp);
    lampMat.disableLighting = true;
    materials.push(lampMat);
    lampMesh.material = lampMat;
    lampMesh.freezeWorldMatrix();
    meshes.push(lampMesh);
  }
  const netMesh = nets.build("arenaNets", scene);
  if (netMesh) {
    const netMat = new StandardMaterial("arenaNetMat", scene);
    netMat.diffuseColor = Color3.White();
    netMat.emissiveColor = new Color3(0.4, 0.4, 0.4);
    netMat.alpha = 0.42;
    netMat.backFaceCulling = false;
    materials.push(netMat);
    netMesh.material = netMat;
    netMesh.freezeWorldMatrix();
    meshes.push(netMesh);
  }
  const glassMesh = glass.build("arenaGlass", scene);
  if (glassMesh) {
    const glassMat = new StandardMaterial("arenaGlassMat", scene);
    glassMat.diffuseColor = Color3.FromHexString("#DDE8F2");
    glassMat.alpha = 0.18;
    glassMat.specularColor = new Color3(0.6, 0.6, 0.6);
    glassMat.backFaceCulling = false;
    materials.push(glassMat);
    glassMesh.material = glassMat;
    glassMesh.freezeWorldMatrix();
    meshes.push(glassMesh);
  }
  for (const m of meshes) m.isPickable = false;

  return {
    meshes,
    casters,
    fieldTexture,
    repaintField,
    screens: { texture: screenTex, width: screenTex.getSize().width, height: screenTex.getSize().height },
    dispose() {
      for (const m of meshes) m.dispose();
      for (const m of materials) m.dispose();
      fieldTexture.dispose();
      screenTex.dispose();
    },
  };
}

// ---------------------------------------------------------------------------------------------
// The crowd
// ---------------------------------------------------------------------------------------------

export interface CrowdHandle {
  meshes: Mesh[];
  /** Fans' colours follow the teams (a new opponent each game). */
  setTeams(home: ArenaTeam, away: ArenaTeam): void;
  /**
   * Real fans sit in these fan seats: the drawn crowd leaves them free. `clear` = the local fan's seat: the drawn crowd
   * also leaves the rows right behind it, where the walk camera looks over the fan's shoulder.
   */
  setTaken(seatIndices: ReadonlySet<number>, clear?: { x: number; z: number; facing: number } | null): void;
  /**
   * Bounce with the noise of each side (0-100), and jump up in a ripple around each fan cheering now (where they are
   * and how long ago they cheered, ms).
   */
  animate(nowMs: number, home: number, away: number, cheers?: ReadonlyArray<{ x: number; z: number; ageMs: number }>): void;
  count(): number;
  dispose(): void;
}

interface Spot {
  x: number;
  y: number;
  z: number;
  facing: number;
  /** Fan seat index (home / away sections), else -1. */
  seat: number;
  side: 0 | 1 | 2;
  hash: number;
}

function hash01(n: number): number {
  let h = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

const CIVVIES = ["#D9D3C7", "#2B2B30", "#7A1F3D", "#13897F", "#E8562A", "#5F6773", "#F2D6A2", "#1F3FA8"];

export function buildCrowd(scene: Scene, kind: ArenaKind, home: ArenaTeam, away: ArenaTeam, density = 0.62): CrowdHandle {
  const l = arenaLayoutOf(kind);
  const spots: Spot[] = [];
  let seatIndex = 0;
  let place = 0;
  for (const s of l.stands) {
    const side: 0 | 1 | 2 = s.section === "home" ? 0 : s.section === "away" ? 1 : 2;
    for (const p of arenaStandSpots(s)) {
      const seat = s.section === "crowd" ? -1 : seatIndex++;
      const hsh = hash01(place++ * 7 + 13);
      if (hsh > density) continue;
      spots.push({ x: p.x, y: p.y, z: p.z, facing: p.facing, seat, side, hash: hsh / density });
    }
  }
  const mat = new StandardMaterial("arenaCrowdMat", scene);
  mat.diffuseColor = Color3.White();
  mat.specularColor = Color3.Black();
  mat.ambientColor = Color3.White();
  // A seated figure: torso and thighs (shirt colour) and a head (skin), each a unit box scaled in the instance matrix.
  const body = CreateBox("arenaCrowdBody", { width: 0.44, height: 0.62, depth: 0.3 }, scene);
  body.bakeTransformIntoVertices(Matrix.Translation(0, 0.62 / 2 + 0.38, -0.02));
  const head = CreateBox("arenaCrowdHead", { size: 0.22 }, scene);
  head.bakeTransformIntoVertices(Matrix.Translation(0, 1.12, -0.02));
  for (const m of [body, head]) {
    m.material = mat;
    m.isPickable = false;
    m.alwaysSelectAsActiveMesh = true;
  }
  let visible: Spot[] = [];
  let base = new Float32Array(0);
  let matrices = new Float32Array(0);
  let taken: ReadonlySet<number> = new Set();
  let clearKey = "";
  let corridor: { x: number; z: number; facing: number } | null = null;
  let teams = { home, away };

  /** Behind the local fan's seat, within the camera's corridor (1.4 m either side, up to 10 m back). */
  const inCorridor = (s: Spot): boolean => {
    if (!corridor) return false;
    const dx = s.x - corridor.x;
    const dz = s.z - corridor.z;
    const back = -(dx * Math.sin(corridor.facing) + dz * Math.cos(corridor.facing));
    const side = Math.abs(dx * Math.cos(corridor.facing) - dz * Math.sin(corridor.facing));
    return back > 0.2 && back < 10 && side < 1.4;
  };

  const rebuild = () => {
    visible = spots.filter((s) => (s.seat < 0 || !taken.has(s.seat)) && !inCorridor(s));
    base = new Float32Array(visible.length * 16);
    visible.forEach((s, i) => {
      const c = Math.cos(s.facing);
      const sn = Math.sin(s.facing);
      // Rotation about y by `facing` (Babylon's RotationY, row-major) and the translation.
      base.set([c, 0, -sn, 0, 0, 1, 0, 0, sn, 0, c, 0, s.x, s.y, s.z, 1], i * 16);
    });
    matrices = base.slice();
    body.thinInstanceSetBuffer("matrix", matrices, 16, false);
    head.thinInstanceSetBuffer("matrix", matrices, 16, false);
    const shirts = new Float32Array(visible.length * 4);
    const skins = new Float32Array(visible.length * 4);
    visible.forEach((s, i) => {
      const h = s.hash;
      const pick = s.side === 0 ? (h < 0.72 ? teams.home.primary : h < 0.86 ? teams.home.secondary : CIVVIES[Math.floor(h * 97) % CIVVIES.length]!) : s.side === 1 ? (h < 0.6 ? teams.away.primary : h < 0.78 ? teams.away.secondary : CIVVIES[Math.floor(h * 97) % CIVVIES.length]!) : h < 0.45 ? teams.home.primary : h < 0.62 ? teams.away.primary : CIVVIES[Math.floor(h * 97) % CIVVIES.length]!;
      const c = Color3.FromHexString(pick);
      shirts.set([c.r, c.g, c.b, 1], i * 4);
      const skin = Color3.FromHexString(["#F2C6A0", "#E0A97E", "#C68A5E", "#9C6643", "#7A4B2E", "#5A3622"][Math.floor(h * 61) % 6]!);
      skins.set([skin.r, skin.g, skin.b, 1], i * 4);
    });
    body.thinInstanceSetBuffer("color", shirts, 4, false);
    head.thinInstanceSetBuffer("color", skins, 4, false);
  };
  rebuild();

  let lastAnim = 0;
  let resting = true;
  return {
    meshes: [body, head],
    setTeams(h, a) {
      teams = { home: h, away: a };
      rebuild();
    },
    setTaken(seats, clear = null) {
      const key = clear ? `${clear.x},${clear.z},${clear.facing}` : "";
      const same = key === clearKey && seats.size === taken.size && [...seats].every((s) => taken.has(s));
      if (same) return;
      taken = new Set(seats);
      clearKey = key;
      corridor = clear;
      rebuild();
    },
    animate(nowMs, homeLevel, awayLevel, cheers = []) {
      // At most 30 updates a second, and none while everyone sits still.
      if (nowMs - lastAnim < 33) return;
      lastAnim = nowMs;
      const amp = [Math.max(0, homeLevel - 25) / 75, Math.max(0, awayLevel - 25) / 75, Math.max(0, Math.max(homeLevel, awayLevel) - 40) / 120];
      // The most recent cheers whose ripple is still running.
      const ripples = cheers.filter((c) => c.ageMs > 0 && c.ageMs < CROWD_RIPPLE.ms + CROWD_RIPPLE.radius * CROWD_RIPPLE.delayPerMetre).slice(-16);
      if (amp.every((a) => a <= 0) && ripples.length === 0) {
        if (!resting) {
          matrices.set(base);
          body.thinInstanceBufferUpdated("matrix");
          head.thinInstanceBufferUpdated("matrix");
          resting = true;
        }
        return;
      }
      resting = false;
      const t = nowMs / 1000;
      for (let i = 0; i < visible.length; i++) {
        const s = visible[i]!;
        const a = amp[s.side]!;
        let up = a > 0 ? Math.max(0, Math.sin(t * 9 + s.hash * 40)) * 0.32 * a * (0.5 + s.hash) : 0;
        for (const r of ripples) {
          const dx = s.x - r.x;
          const dz = s.z - r.z;
          if (Math.abs(dx) > CROWD_RIPPLE.radius || Math.abs(dz) > CROWD_RIPPLE.radius) continue;
          up = Math.max(up, crowdRippleLift(Math.hypot(dx, dz), r.ageMs) * (0.75 + 0.5 * s.hash));
        }
        matrices[i * 16 + 13] = base[i * 16 + 13]! + up;
      }
      body.thinInstanceBufferUpdated("matrix");
      head.thinInstanceBufferUpdated("matrix");
    },
    count: () => visible.length,
    dispose() {
      body.dispose();
      head.dispose();
      mat.dispose();
    },
  };
}
